import { Serializable } from '@playcastdotio/CoreTypes';
import { generators, PlaycastMessage, PlaycastMessageStateReplication } from '@playcastdotio/Messaging';

export type StateStore<T extends Serializable> = {
  currentState: T;
  listeners: Record<string, (state: T) => void>;
  addListener: (callback: (state: T) => void) => string;
  removeListener: (listenerId?: string) => void;
  updateState: (state: T) => void;
  reset: (destroyListeners?: boolean) => void;
};

export type NonSerializableStateStore<T> = {
  currentState: T;
  listeners: Record<string, (state: T) => void>;
  addListener: (callback: (state: T) => void) => string;
  removeListener: (listenerId?: string) => void;
  updateState: (state: T) => void;
  reset: (destroyListeners?: boolean) => void;
};

export type ReplicationTarget = 'hostBackend' | 'hostFrontend' | 'remoteHost' | 'remoteClient' | 'player';

const DEBUG = false;

const replicationStateChangeCallbacks: {
  [key in ReplicationTarget]: Record<
    string,
    (message: PlaycastMessage<PlaycastMessageStateReplication<string>>, splitReplicationKey?: string) => void
  >;
} = {} as any;

const allReplicatedStateStores: Record<
  ReplicationTarget,
  {
    channel: string;
    store: StateStore<any>;
  }[]
> = {} as any;

export type StateStoreCreationConfig<T> = {
  replicate?: { channel: string; targets: ReplicationTarget[]; rateLimit?: number };
  splitReplicate?: { channel: string; targets: ReplicationTarget[]; splitKey?: string[] };
  transformFunction?: (oldState: T, incomingState: T) => T;
};

export const makeStateStore = <T extends Serializable>(initialState: T, config?: StateStoreCreationConfig<T>): StateStore<T> => {
  const copyState = (state: T): T => JSON.parse(JSON.stringify(state));
  const initialStateCopy = copyState(initialState);

  const stateStore: StateStore<T> = {
    currentState: initialState,
    listeners: {},
    addListener: (cb) => {
      const listenerId = Math.random().toString();
      stateStore.listeners[listenerId] = cb;
      return listenerId;
    },
    removeListener: (id) => {
      if (id) delete stateStore.listeners[id];
      if (!id) stateStore.listeners = {};
    },
    updateState: (state) => {
      if (config?.transformFunction) state = config.transformFunction(stateStore.currentState, state);
      stateStore.currentState = state;
      Object.values(stateStore.listeners).forEach((cb) => cb(state));
    },
    reset: (destroyListeners?: boolean) => {
      stateStore.currentState = initialStateCopy;
      if (destroyListeners) {
        stateStore.listeners = {};
        addReplicators();
      }
    },
  };
  const addReplicators = () => {
    if (config?.replicate) {
      const channel = config.replicate.channel;
      config.replicate.targets.forEach((target) => {
        let lastUpdate = 0;
        let pendingUpdate: T | null = null;
        let timeoutId: NodeJS.Timeout | null = null;

        const sendUpdate = (state: T) => {
          if (DEBUG) console.log('[StateStore] Replication for', channel, 'to', target, state);
          const message = generators.state.replication(channel, state);
          if (!replicationStateChangeCallbacks[target]) replicationStateChangeCallbacks[target] = {};
          Object.values(replicationStateChangeCallbacks[target]).forEach((cb) => cb(message));
          lastUpdate = Date.now();
          pendingUpdate = null;
        };

        stateStore.addListener((state) => {
          if (config.replicate?.rateLimit !== undefined) {
            const now = Date.now();
            if (now - lastUpdate < config.replicate.rateLimit) {
              pendingUpdate = state;
              if (!timeoutId) {
                timeoutId = setTimeout(
                  () => {
                    if (pendingUpdate !== null) {
                      sendUpdate(pendingUpdate);
                    }
                    timeoutId = null;
                  },
                  config.replicate.rateLimit - (now - lastUpdate),
                );
              }
            } else {
              sendUpdate(state);
            }
          } else {
            sendUpdate(state);
          }
        });

        const channelObject = allReplicatedStateStores[target];
        if (!channelObject) allReplicatedStateStores[target] = [];
        allReplicatedStateStores[target].push({ channel, store: stateStore });
      });
    }
    if (config?.splitReplicate) {
      const channel = config.splitReplicate.channel;
      config.splitReplicate.targets.forEach((target) => {
        let cachedState = stateStore.currentState;
        stateStore.addListener((state) => {
          const splitReplicate = config.splitReplicate;
          if (!splitReplicate) return;
          const splitKey = splitReplicate.splitKey || [];
          let nestedState: Serializable = state;
          let nestedRecord: { [key: string]: Serializable } = {};
          if (splitKey.length === 0) {
            if (typeof state === 'object') {
              if (!Array.isArray(state)) {
                if (state) {
                  nestedRecord = state;
                }
              }
            }
          }
          for (let i = 0; i < splitKey.length; i++) {
            if (typeof nestedState !== 'object') return;
            if (!nestedState) return;
            if (Array.isArray(nestedState)) return;
            nestedRecord = nestedState;
            nestedState = nestedState[splitKey[i]];
            if (nestedState === undefined) {
              console.error('splitKey does not point to an object');
              return;
            }
            if (splitKey.length - 1 === i) {
              const maybeResult = nestedState;
              if (typeof maybeResult === 'object') {
                if (!Array.isArray(maybeResult)) {
                  if (maybeResult) {
                    nestedRecord = maybeResult;
                    break;
                  }
                }
              }
              console.error('splitKey does not point to an object');
            }
          }
          if (!nestedRecord) {
            console.error('splitKey does not point to an object');
            return;
          }
          const splitReplicationKey = Object.keys(nestedRecord);
          splitReplicationKey.forEach((key) => {
            const newState = nestedRecord[key];
            if (DEBUG) console.log('[StateStore] Split replication', key, newState);
            if (newState !== cachedState) {
              const message = generators.state.replication(channel, newState);
              if (!replicationStateChangeCallbacks[target]) replicationStateChangeCallbacks[target] = {};
              Object.values(replicationStateChangeCallbacks[target]).forEach((cb) => cb(message, key));
            }
          });
          cachedState = state;
        });
        // const channelObject = allReplicatedStateStores[target];
        // if (!channelObject) allReplicatedStateStores[target] = [];
        // allReplicatedStateStores[target].push({ channel, store: stateStore });
      });
    }
  };
  addReplicators();
  return stateStore;
};

export const makeNonSerializableStateStore = <T>(initialState: T): NonSerializableStateStore<T> => {
  const stateStore: NonSerializableStateStore<T> = {
    currentState: initialState,
    listeners: {},
    addListener: (cb) => {
      const listenerId = Math.random().toString();
      stateStore.listeners[listenerId] = cb;
      return listenerId;
    },
    removeListener: (id) => {
      if (id) delete stateStore.listeners[id];
      if (!id) stateStore.listeners = {};
    },
    updateState: (state) => {
      stateStore.currentState = state;
      Object.values(stateStore.listeners).forEach((cb) => cb(state));
    },
    reset: (destroyListeners?: boolean) => {
      stateStore.currentState = initialState;
      if (destroyListeners) {
        stateStore.listeners = {};
      }
    },
  };
  return stateStore;
};

export const addReplicationStateChangeCallback = (
  target: ReplicationTarget,
  callback: (message: PlaycastMessage<PlaycastMessageStateReplication<string>>, splitReplicationKey?: string) => void,
): string => {
  const listenerId = Math.random().toString();
  if (!replicationStateChangeCallbacks[target]) replicationStateChangeCallbacks[target] = {};
  replicationStateChangeCallbacks[target][listenerId] = callback;
  return listenerId;
};

export const removeReplicationStateChangeCallback = (target: ReplicationTarget, listenerId: string): void => {
  delete replicationStateChangeCallbacks[target][listenerId];
};

export const removeAllReplicationStateChangeCallbacks = (target?: ReplicationTarget): void => {
  if (target) {
    replicationStateChangeCallbacks[target] = {};
  } else {
    Object.values(replicationStateChangeCallbacks).forEach((target) => {
      target = {};
    });
  }
};

export const getNotifyMessages = (targets: ReplicationTarget[]) => {
  const output: Record<ReplicationTarget, PlaycastMessage<PlaycastMessageStateReplication<string>>[]> = {} as any;
  targets.forEach((target) => {
    if (!output[target]) output[target] = [];
    const channels = allReplicatedStateStores[target];
    if (channels) {
      channels.forEach((channel) => {
        if (DEBUG) console.log('[StateStore] Notify', target, channel.channel, channel.store.currentState);
        output[target].push(generators.state.replication(channel.channel, channel.store.currentState));
      });
    }
  });
  return output;
};
